import { getRealName } from './orderIdentity'

export const TAX_RATES = {
  QC: 14.975,
  ON: 13,
  'NO TAXES': 0,
  NB: 15,
  BC: 5,
  NL: 15,
  NS: 14,
  PE: 15,
  MB: 5,
  SK: 5,
  AB: 5,
  NT: 5,
  NU: 5,
  YT: 5,
  TPS: 5
}

export function getOrderNumber (order) {
  return order.orderNumber || order['.key'] || order.key || ''
}

export function getInvoiceOrders (invoice) {
  return invoice && Array.isArray(invoice.orders) ? invoice.orders : [invoice]
}

export function isCanadianCountry (country) {
  const normalizedCountry = (country || '').toString().trim().toUpperCase()
  return normalizedCountry === '' ||
    normalizedCountry === 'CA' ||
    normalizedCountry === 'CAN' ||
    normalizedCountry === 'CANADA'
}

export function normalizeProvince (province) {
  return (province || '').toString().trim().toUpperCase()
}

const CANADIAN_PROVINCES = Object.keys(TAX_RATES).filter(code => code !== 'NO TAXES' && code !== 'TPS')

export function isForeignLocation (province, country) {
  if (!isCanadianCountry(country)) return true
  const normalized = normalizeProvince(province)
  return normalized !== '' && !CANADIAN_PROVINCES.includes(normalized)
}

export function shouldUseZeroTax (order) {
  return order.zeroTax === true ||
    order.payerZeroTax === true ||
    isForeignLocation(order.transmitterProvince, order.transmitterCountry) ||
    isForeignLocation(order.receiverProvince, order.receiverCountry)
}

// From = transmitterProvince, To = receiverProvince:
// US/foreign on either end -> NO TAXES | QC->QC -> QC | elsewhere->QC -> TPS | ->other province -> that province's rate
export function getDefaultTaxCode (order) {
  if (shouldUseZeroTax(order)) return 'NO TAXES'

  const from = normalizeProvince(order.transmitterProvince)
  const to = normalizeProvince(order.receiverProvince)

  if (to === 'QC') return from === 'QC' ? 'QC' : 'TPS'
  if (TAX_RATES[to] !== undefined) return to

  return 'TPS'
}

export function getTaxRate (invoice) {
  const taxCode = invoice.taxes || getDefaultTaxCode(invoice)
  return TAX_RATES[taxCode] !== undefined ? Number(TAX_RATES[taxCode]) : 0
}

export function getFuelChargePercent (order, defaultFuelCharge = 0) {
  return order.fuelCharge !== undefined && order.fuelCharge !== null && order.fuelCharge !== ''
    ? order.fuelCharge
    : defaultFuelCharge || 0
}

export function getOrderFeesTotal (order) {
  if (!Array.isArray(order.fees)) return 0
  return order.fees.reduce((total, fee) => total + (Number(fee.amount) || 0), 0)
}

export function getOrderFuelAmount (order, defaultFuelCharge = 0) {
  if (order.includedFuelCharge || order.applyFuelCharge === false) return 0
  return (Number(order.price) * Number(getFuelChargePercent(order, defaultFuelCharge))) / 100
}

export function getOrderSubtotal (order, defaultFuelCharge = 0) {
  return (Number(order.price) || 0) + getOrderFuelAmount(order, defaultFuelCharge) + getOrderFeesTotal(order)
}

export function getInvoiceSubtotal (invoice, defaultFuelCharge = 0) {
  return getInvoiceOrders(invoice).reduce((total, order) => total + getOrderSubtotal(order, defaultFuelCharge), 0)
}

export function getInvoiceTaxAmount (invoice, defaultFuelCharge = 0) {
  return (getInvoiceSubtotal(invoice, defaultFuelCharge) * getTaxRate(invoice)) / 100
}

export function getInvoiceTotal (invoice, defaultFuelCharge = 0) {
  return getInvoiceSubtotal(invoice, defaultFuelCharge) + getInvoiceTaxAmount(invoice, defaultFuelCharge)
}

export function getOdooReference (order) {
  return order.orderBill || order.billNum || order.billId || order.odooBillNumber || order.odooOrderNumber || ''
}

export function normalizePrice (value) {
  const parsedPrice = Number(value)
  if (isNaN(parsedPrice)) return 0
  return Number(parsedPrice.toFixed(2))
}

export function formatDateYMD (date) {
  const fdate = new Date(date)
  return fdate.getFullYear() + '-' + ('0' + (fdate.getMonth() + 1)).slice(-2) + '-' + ('0' + fdate.getDate()).slice(-2)
}

export function buildOdooInvoiceGroupId (companyUID) {
  return `${companyUID}-${Date.now()}-${Math.floor(Math.random() * 100000)}`
}

function buildDeliveryDescription (order) {
  const route = `${getRealName(order, 'transmitter')} to ${getRealName(order, 'receiver')}`
  if (!order.isDryBoxOrder) {
    return `${order.make || ''} ${order.model || ''} ${order.year || ''} - VIN: ${order.vin || ''} ${route}`.trim()
  }
  const parts = [`Pallet ${order.make || '?'}x${order.model || '?'}x${order.trim || '?'} in x ${order.year || 1}`]
  if (Number(order.weight)) parts.push(`${order.weight} lbs`)
  if (Number(order.linearFeet)) parts.push(`${order.linearFeet} lin. ft`)
  if (order.isFullLoad) parts.push('Full load')
  return `${parts.join(' - ')} - Order #: ${order.vin || ''} ${route}`.trim()
}

// Dry box: bill per pallet when the line price splits evenly to the cent, else keep 1 x total
function getDeliveryQuantity (order) {
  const total = Number(order.price) || 0
  const qty = parseInt(order.year, 10) || 1
  const unitPrice = normalizePrice(total / qty)
  if (!order.isDryBoxOrder || qty <= 1 || normalizePrice(unitPrice * qty) !== normalizePrice(total)) {
    return { quantity: 1, unit_price: total, total }
  }
  return { quantity: qty, unit_price: unitPrice, total }
}

export function buildOrderOdooLines (order, defaultFuelCharge = 0) {
  const orderNumber = getOrderNumber(order)
  const lines = [
    {
      type: 'delivery',
      order_number: orderNumber,
      description: buildDeliveryDescription(order),
      ...getDeliveryQuantity(order)
    }
  ]

  if (Array.isArray(order.fees)) {
    order.fees.forEach((fee) => {
      const amount = Number(fee.amount) || 0
      if (amount) {
        lines.push({
          type: 'fee',
          fee_type: fee.type || fee.text || 'Other',
          order_number: orderNumber,
          description: fee.text || fee.type || 'Fee',
          quantity: 1,
          unit_price: amount,
          total: amount
        })
      }
    })
  }

  const fuelAmount = getOrderFuelAmount(order, defaultFuelCharge)
  if (fuelAmount) {
    lines.push({
      type: 'fuel_surcharge',
      order_number: orderNumber,
      description: `Fuel Surcharge ${getFuelChargePercent(order, defaultFuelCharge)}%`,
      quantity: 1,
      unit_price: fuelAmount,
      total: fuelAmount
    })
  }

  const odooComment = typeof order.odooComment === 'string' ? order.odooComment.trim() : ''
  if (odooComment) {
    lines.push({
      type: 'comment',
      order_number: orderNumber,
      description: odooComment,
      quantity: 1,
      unit_price: 0,
      total: 0
    })
  }

  return lines
}

export function buildOdooInvoicePayload (invoice, batchId, defaultFuelCharge = 0) {
  const orders = getInvoiceOrders(invoice)
  const firstOrder = orders[0] || invoice
  return {
    batch_id: batchId,
    invoice_key: invoice.invoiceKey || getOrderNumber(invoice),
    invoice_group_id: invoice.odooInvoiceGroupId || '',
    grouped: orders.length > 1,
    billing_date: invoice.billingDate || '',
    payer_id: invoice.payer || '',
    payer_name: getRealName(firstOrder, 'payer') || invoice.payerName || '',
    odoo_partner_id: invoice.payerOdooPartnerId || firstOrder.payerOdooPartnerId || '',
    odoo_partner_name: invoice.payerOdooPartnerName || firstOrder.payerOdooPartnerName || '',
    tax_code: invoice.taxes || getDefaultTaxCode(invoice),
    tax_rate: getTaxRate(invoice),
    subtotal: normalizePrice(getInvoiceSubtotal(invoice, defaultFuelCharge)),
    tax_amount: normalizePrice(getInvoiceTaxAmount(invoice, defaultFuelCharge)),
    total: normalizePrice(getInvoiceTotal(invoice, defaultFuelCharge)),
    order_numbers: orders.map(order => getOrderNumber(order)),
    lines: orders.reduce((lines, order) => lines.concat(buildOrderOdooLines(order, defaultFuelCharge)), [])
  }
}
